import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Send, Phone, MapPin, Building, BadgeCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Preview-then-send dispatch dialog used from the KDS Ready column and the
 * POS order detail. The cashier first sees the list of drivers who WILL be
 * SMS'd (with city + on-shift + distance if known), can switch strategy, and
 * only THEN clicks "Send to N drivers" to actually broadcast.
 *
 * Props:
 *  - open / onOpenChange
 *  - order: the order doc (needs id + order_number + branch_id)
 *  - onDispatched(result): called after a successful dispatch
 */
export default function DispatchPreviewDialog({ open, onOpenChange, order, onDispatched }) {
  const [strategy, setStrategy] = useState('branch');
  const [radius, setRadius] = useState('');
  const [preview, setPreview] = useState(null); // { candidates: [], ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!order?.id || !open) return;
    setPreviewLoading(true);
    try {
      const params = { strategy };
      if (strategy === 'proximity' && radius) params.radius_km = Number(radius);
      const { data } = await api.get(`/delivery/${order.id}/dispatch-candidates`, { params });
      setPreview(data);
    } catch (e) {
      setPreview({ candidates: [] });
      toast.error(e?.response?.data?.detail || 'Could not load candidates');
    } finally {
      setPreviewLoading(false);
    }
  }, [order?.id, strategy, radius, open]);

  useEffect(() => { if (open) { load(); } else { setPreview(null); setStrategy('branch'); setRadius(''); } }, [open, load]);

  const candidates = preview?.candidates || [];
  const onShiftCount = candidates.filter((c) => c.on_shift).length;

  const send = async () => {
    if (!candidates.length) { toast.error('No drivers to notify'); return; }
    setSending(true);
    try {
      const payload = { strategy };
      if (strategy === 'proximity' && radius) payload.radius_km = Number(radius);
      const { data } = await api.post(`/delivery/${order.id}/dispatch`, payload);
      toast.success(`Dispatched to ${data.candidates?.length || 0} driver(s)`);
      onDispatched?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dispatch');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dispatch-preview-dialog" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dispatch delivery #{order?.order_number}</DialogTitle>
          <DialogDescription>
            Choose how to find a driver. The list below is who will be notified via SMS — first to accept wins.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Strategy</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: 'branch', label: 'Same city / branch', hint: 'Match by branch city' },
                { k: 'proximity', label: 'Proximity', hint: 'Closest first (needs live location)' },
                { k: 'all', label: 'All drivers', hint: 'Broadcast (use sparingly)' },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  data-testid={`dispatch-strategy-${opt.k}`}
                  onClick={() => setStrategy(opt.k)}
                  className={`rounded-md border px-3 py-2 text-left text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${strategy === opt.k ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted'}`}
                >
                  {opt.label}
                  <p className={`text-[10px] font-normal ${strategy === opt.k ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{opt.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {strategy === 'proximity' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Max radius (km, optional)</Label>
              <Input type="number" min="0" step="0.5" value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="e.g. 5" />
              {preview && !preview.has_dropoff_geo && (
                <p className="text-[11px] text-amber-700">Heads-up: this delivery address has no lat/lng — proximity ranking falls back to branch filtering.</p>
              )}
            </div>
          )}

          <div className="rounded-md border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
              <span className="font-semibold uppercase tracking-wider text-muted-foreground">
                Candidates ({candidates.length})
              </span>
              <span className="text-muted-foreground">{onShiftCount} on shift · {candidates.length - onShiftCount} off</span>
            </div>
            <ScrollArea className="max-h-[36vh]">
              {previewLoading ? (
                <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading candidates…
                </div>
              ) : !candidates.length ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No eligible drivers for this strategy. Try “All drivers” or check city assignments.
                </div>
              ) : (
                <ul data-testid="dispatch-candidates-list" className="divide-y divide-border">
                  {candidates.map((c) => (
                    <li key={c.id} data-testid="dispatch-candidate-row" className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${c.on_shift ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{c.full_name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {c.phone ? (<><Phone className="mr-0.5 inline h-3 w-3" /> {c.phone}</>) : 'No phone'}
                          {c.city && <> · <MapPin className="mx-1 inline h-3 w-3" /> {c.city}</>}
                          {c.branch_id && <> · <Building className="mx-1 inline h-3 w-3" /> branch</>}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {c.distance_km != null && (
                          <Badge variant="secondary" className="font-mono">{c.distance_km.toFixed(1)} km</Badge>
                        )}
                        {c.on_shift ? (
                          <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-900"><BadgeCheck className="mr-1 h-3 w-3" /> On shift</Badge>
                        ) : (
                          <Badge variant="secondary"><ShieldOff className="mr-1 h-3 w-3" /> Off</Badge>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={sending}>Cancel</Button>
          <Button data-testid="dispatch-send-button" onClick={send} disabled={sending || previewLoading || !candidates.length}>
            {sending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-1 h-4 w-4" /> Send to {candidates.length} driver{candidates.length === 1 ? '' : 's'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
